<script setup lang="ts">
import type { AllowedResources } from '#layers/access/utils/entitlement-codec';
import type { ResponseEnvelope } from '#layers/base/public';
import type { MonospaceRole } from '#layers/data-model/public';
import type { SelectMenuProps } from '#layers/ui/public';
import { computedAsync } from '@vueuse/core';
import { computed, ref } from 'vue';
import { AllowedResourcesKinds, allowsAnyResource, allResourcesAllowed } from '#layers/access/utils/entitlement-codec';
import { stringifyQuery, useWorkspaceFetchFactory } from '#layers/base/public';
import { useTranslationUtils } from '#layers/i18n/public';

const props = withDefaults(defineProps<{
	allowedRoleIds?: AllowedResources;
	placeholder?: string;
	size?: SelectMenuProps['size'];
	workspaceSlug: string;
}>(), {
	allowedRoleIds: () => allResourcesAllowed,
	size: 'md',
});

const model = defineModel<string[]>({ default: () => [] });

// A picker can target a workspace other than the active runtime. Keep those roles out of the
// active workspace's entity cache by reading the explicit workspace endpoint directly.
const createWorkspaceFetch = useWorkspaceFetchFactory();
const { translateString } = useTranslationUtils();
const loading = ref(false);

const roles = computedAsync<MonospaceRole[]>(async (onCancel) => {
	const { allowedRoleIds, workspaceSlug } = props;
	if (!allowsAnyResource(allowedRoleIds)) return [];

	const controller = new AbortController();
	onCancel(() => controller.abort());
	const workspaceFetch = createWorkspaceFetch({ signal: controller.signal, workspaceSlug });

	try {
		const response = await workspaceFetch<ResponseEnvelope<MonospaceRole[]>>('roles', {
			query: stringifyQuery({
				fields: 'id,name',
				filter: allowedRoleIds.kind === AllowedResourcesKinds.All ? undefined : { id: { _in: [...allowedRoleIds.ids] } },
			}),
		});
		return response.data ?? [];
	}
	catch {
		// computedAsync discards superseded results itself, so this only lands for real failures.
		return [];
	}
}, [], { evaluating: loading });

// Protected roles use `$t:` placeholders while custom roles store user-authored names.
const assignableRoles = computed(() => roles.value.map((role) => ({ ...role, name: translateString(role.name) })));
</script>

<template>
	<DSelectMenu
		v-model="model"
		value-key="id"
		label-key="name"
		:items="assignableRoles"
		multiple
		:placeholder
		:disabled="loading"
		:size
	/>
</template>
